import moment from 'moment';

import BaseModel from './Base.model';
import UserModel from './User.model';
import CustomerModel from './Customer.model';
import DemoCarModel from './DemoCar.model';

import {
  formatDate,
  formatDateTime,
  formatDatetimeDB,
  formatDBDate,
} from '../../../../utils/date';

export class TestDriveModel extends BaseModel {
  /**
   * @static
   * @readonly
   */
  static STATUS = {
    REGISTERED: 1,
    CONFIRMED: 2,
    COMPLETED: 3,
    CANCELLED: 4,
  };

  /**
   * Reason definitions for test drives
   * @static
   * @readonly
   */
  static REASON = {
    CUSTOMER_SERVICE: 1,
    TEST_DRIVE_EVENT: 2,
    LEADERSHIP_USAGE: 3,
    COMPANY_BUSINESS: 4,
    OTHER: 5,
  };

  /**
   * Status metadata map
   * @static
   * @readonly
   */
  static STATUS_MAP = {
    [TestDriveModel.STATUS.REGISTERED]: {
      id: TestDriveModel.STATUS.REGISTERED,
      text: 'Đăng ký',
      class: 'label-light-warning',
      icon: 'flaticon-clock',
      description: 'Đăng ký lái thử mới',
    },

    [TestDriveModel.STATUS.CONFIRMED]: {
      id: TestDriveModel.STATUS.CONFIRMED,
      text: 'Xác nhận',
      class: 'label-light-primary',
      icon: 'flaticon2-check-mark',
      description: 'Đã xác nhận lịch lái thử',
    },

    [TestDriveModel.STATUS.COMPLETED]: {
      id: TestDriveModel.STATUS.COMPLETED,
      text: 'Hoàn thành',
      class: 'label-light-success',
      icon: 'flaticon2-checkmark',
      description: 'Đã hoàn thành lái thử',
    },

    [TestDriveModel.STATUS.CANCELLED]: {
      id: TestDriveModel.STATUS.CANCELLED,
      text: 'Hủy',
      class: 'label-light-danger',
      icon: 'flaticon2-cross',
      description: 'Đã hủy lái thử',
    },
  };

  /**
   * Reason metadata map
   * @static
   * @readonly
   */
  static REASON_MAP = {
    [TestDriveModel.REASON.CUSTOMER_SERVICE]: {
      id: TestDriveModel.REASON.CUSTOMER_SERVICE,
      text: 'Phục vụ khách hàng',
      description: 'Sử dụng xe để phục vụ khách hàng',
    },
    [TestDriveModel.REASON.TEST_DRIVE_EVENT]: {
      id: TestDriveModel.REASON.TEST_DRIVE_EVENT,
      text: 'Tổ chức sự kiện lái thử',
      description: 'Sử dụng xe cho các sự kiện lái thử',
    },
    [TestDriveModel.REASON.LEADERSHIP_USAGE]: {
      id: TestDriveModel.REASON.LEADERSHIP_USAGE,
      text: 'Ban lãnh đạo sử dụng',
      description: 'Xe được sử dụng bởi ban lãnh đạo',
    },
    [TestDriveModel.REASON.COMPANY_BUSINESS]: {
      id: TestDriveModel.REASON.COMPANY_BUSINESS,
      text: 'Phục vụ công việc công ty',
      description: 'Sử dụng xe cho các công việc của công ty',
    },
    [TestDriveModel.REASON.OTHER]: {
      id: TestDriveModel.REASON.OTHER,
      text: 'Khác',
      description: 'Lý do khác',
    },
  };

  /**
   * Status transition map - định nghĩa các trạng thái có thể chuyển đổi tiếp theo từ mỗi trạng thái
   * @static
   * @readonly
   */
  static STATUS_TRANSITIONS = {
    [TestDriveModel.STATUS.REGISTERED]: [
      TestDriveModel.STATUS.CONFIRMED,
      TestDriveModel.STATUS.CANCELLED,
    ],
    [TestDriveModel.STATUS.CONFIRMED]: [
      TestDriveModel.STATUS.COMPLETED,
      TestDriveModel.STATUS.REGISTERED,
    ],
    [TestDriveModel.STATUS.COMPLETED]: [],
    [TestDriveModel.STATUS.CANCELLED]: [],
  };

  /**
   * Get status object by ID
   * @static
   * @param {number} statusId - Status ID
   * @returns {STATUS} Status object
   */
  static getStatusById(statusId) {
    return (
      TestDriveModel.STATUS_MAP[statusId] ||
      TestDriveModel.STATUS_MAP[TestDriveModel.STATUS.REGISTERED]
    );
  }

  /**
   * Get reason object by ID
   * @static
   * @param {number} reasonId - Reason ID
   * @returns {Object} Reason object
   */
  static getReasonById(reasonId) {
    return (
      TestDriveModel.REASON_MAP[reasonId] ||
      TestDriveModel.REASON_MAP[TestDriveModel.REASON.OTHER]
    );
  }

  /**
   * Get status options for select components
   * @static
   * @returns {Array} Array of status options
   */
  static getStatusOptions() {
    return Object.values(TestDriveModel.STATUS_MAP).map((status) => ({
      id: status.id,
      text: status.text,
    }));
  }

  /**
   * Get reason options for select components
   * @static
   * @returns {Array} Array of reason options
   */
  static getReasonOptions() {
    return Object.values(TestDriveModel.REASON_MAP).map((reason) => ({
      id: reason.id,
      text: reason.text,
    }));
  }

  /**
   * Get available next statuses based on current status
   * @static
   * @param {number} currentStatusId - Current status ID
   * @returns {Array} Array of available next status objects
   */
  static getAvailableNextStatuses(currentStatusId) {
    const nextStatusIds =
      TestDriveModel.STATUS_TRANSITIONS[currentStatusId] || [];
    return nextStatusIds.map((statusId) => TestDriveModel.STATUS_MAP[statusId]);
  }

  /**
   *
   * @param {Object} data
   * @param {string} data.id
   * @param {Date} data.createdAt
   * @param {Date} data.updatedAt
   * @param {string} data.storeId
   * @param {Date} data.registrationDate
   * @param {string} data.demoTicketId
   * @param {DemoCarModel} data.demoTicket
   * @param {Object} data.store
   * @param {UserModel} data.employee
   * @param {CustomerModel} data.customer
   * @param {string} data.productId
   * @param {string} data.productName
   * @param {string} data.productCode
   * @param {string} data.chassisNumber
   * @param {string} data.engineNumber
   * @param {string} data.licensePlate
   * @param {string} data.employeeId
   * @param {string} data.employeeName
   * @param {string} data.reason
   * @param {string} data.purpose
   * @param {Date} data.startTime
   * @param {Date} data.endTime
   * @param {string} data.customerId
   * @param {string} data.customerName
   * @param {string} data.testDriveAddress
   * @param {number} data.kmBeforeDrive
   * @param {number} data.kmAfterDrive
   * @param {number} data.status
   * @param {string} data.notes
   *
   */
  constructor(data = {}) {
    super({
      id: data?.id || '',
      createdAt: data?.createdAt,
      updatedAt: data?.updatedAt,
    });
    this.storeId = data?.storeId || '';
    this.registrationDate = formatDate(data?.registrationDate);
    this.demoTicketId = data?.demoTicketId || '';
    this.productId = data?.productId || '';
    this.employeeId = data?.employeeId || '';
    this.reason = data?.reason || null;
    this.purpose = data?.purpose || '';
    this.startTime = formatDateTime(data?.startTime);
    this.endTime = formatDateTime(data?.endTime);
    this.customerId = data?.customerId || '';
    this.testDriveAddress = data?.testDriveAddress || '';
    this.kmBeforeDrive = data?.kmBeforeDrive
      ? Number.parseFloat(data.kmBeforeDrive)
      : null;
    this.kmAfterDrive = data?.kmAfterDrive
      ? Number.parseFloat(data.kmAfterDrive)
      : null;
    this.status = Number(data?.status) || TestDriveModel.STATUS.REGISTERED;
    this.notes = data?.notes || '';
    this.attachments = data?.attachments || [];

    // Relations
    this.demoTicket = DemoCarModel.fromJson(data?.demoTicket);
    this.employee = UserModel.fromJson(data?.employee);
    this.customer = CustomerModel.fromJson(data?.customer);
    this.store = data?.store;

    // Additional fields
    this.productName = this?.demoTicket?.productName;
    this.productCode = this?.demoTicket?.productCode;
    this.chassisNumber = this?.demoTicket?.chassisNumber;
    this.engineNumber = this?.demoTicket?.engineNumber;

    this.licensePlate = this?.demoTicket?.licensePlate;
    this.currentKm = this?.demoTicket?.currentKm;

    this.storeName = this?.store?.shortName || '';
    this.employeeName = this?.employee?.fullName || '';
    this.customerName = this?.customer?.fullName || '';
  }

  // Kiểm tra thời gian hợp lệ
  isValidTimeRange() {
    if (!this.startTime || !this.endTime) return false;

    const start = moment(this.startTime);
    const end = moment(this.endTime);

    if (!start.isValid() || !end.isValid()) return false;

    // Thời gian kết thúc phải sau thời gian bắt đầu ít nhất 15 phút
    const diffInMinutes = end.diff(start, 'minutes');
    return diffInMinutes >= 15;
  }

  /**
   * Format UUID thành mã phiếu số
   * @param {string} uuid - UUID của phiếu lái thử
   * @param {string|Date} createdAt - Thời gian tạo phiếu
   * @returns {string} - Mã phiếu (VD: 241205001)
   */
  static formatTestDriveCode(uuid, createdAt) {
    if (!uuid || !createdAt) {
      console.error('Invalid UUID or createdAt');
      return '';
    }

    const date = new Date(createdAt);

    // YYMMDD (6 chữ số)
    const dateStr =
      date.getFullYear().toString().slice(-2) +
      String(date.getMonth() + 1).padStart(2, '0') +
      String(date.getDate()).padStart(2, '0');

    // Sử dụng milliseconds + UUID hash để tạo sequence độc nhất
    const milliseconds = date.getMilliseconds();
    const seconds = date.getSeconds();
    const uuidHash = this.hashUuidToSequence(uuid);

    // Tạo sequence 3 chữ số từ: giây (2 chữ số) + hash UUID (1 chữ số)
    const timeComponent = String(
      (seconds * 10 + Math.floor(milliseconds / 100)) % 100,
    ).padStart(2, '0');
    const uuidComponent = String(uuidHash % 10);
    const sequence = timeComponent + uuidComponent;

    return `${dateStr}${sequence}`;
  }

  /**
   * Hash UUID thành số nhỏ để làm component cho sequence
   * @param {string} uuid - UUID cần hash
   * @returns {number} - Số hash
   */
  static hashUuidToSequence(uuid) {
    let hash = 0;
    const cleanUuid = uuid.replace(/-/g, '');

    for (let i = 0; i < cleanUuid.length; i++) {
      const char = cleanUuid.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    return Math.abs(hash);
  }

  /**
   * Chuyển đổi từ dữ liệu JSON thành đối tượng
   * @param {Object} json - Dữ liệu JSON
   * @returns {TestDriveModel} - Đối tượng
   */
  static fromJson(json) {
    return BaseModel.fromJson(json, TestDriveModel);
  }

  /**
   * Chuyển đổi từ danh sách JSON thành danh sách đối tượng
   * @param {Array} jsonArray - Mảng dữ liệu JSON
   * @returns {Array<TestDriveModel>} - Mảng đối tượng
   */
  static fromJsonArray(jsonArray) {
    return BaseModel.fromJsonArray(jsonArray, TestDriveModel);
  }

  /**
   * Chuyển đổi đối tượng thành dữ liệu JSON để gửi lên server
   * @returns {Object} - Dữ liệu JSON
   */
  toJSON() {
    return {
      id: this.id,
      storeId: this.storeId,
      storeName: this.storeName,
      registrationDate: this?.registrationDate
        ? formatDBDate(this.registrationDate)
        : '',
      demoTicketId: this.demoTicketId,
      productId: this.productId,
      productName: this.productName,
      productCode: this.productCode,
      chassisNumber: this.chassisNumber,
      engineNumber: this.engineNumber,
      licensePlate: this.licensePlate,
      employeeId: this.employeeId,
      employeeName: this.employeeName,
      reason: this.reason,
      purpose: this.purpose,
      startTime: formatDatetimeDB(this.startTime),
      endTime: formatDatetimeDB(this.endTime),
      customerId: this.customerId,
      customerName: this.customerName,
      testDriveAddress: this.testDriveAddress,
      kmBeforeDrive: this.kmBeforeDrive,
      kmAfterDrive: this.kmAfterDrive,
      status: this.status,
    };
  }
}
